import React from 'react';

function SaveButton({ isVideo, image, shapes, pins, width, height, productList, id, types, serverValue, setLoading }) {
  // Drawing helpers (match Canvas.js)
  function withRotation(ctx, center, angle, drawFn) {
    ctx.save();
    ctx.translate(center.x, center.y);
    ctx.rotate((angle || 0) * Math.PI / 180);
    ctx.translate(-center.x, -center.y);
    drawFn();
    ctx.restore();
  }
  const drawFreehand = (ctx, points, color, thick, rotation) => {
    if (!points || points.length < 2) return;
    ctx.strokeStyle = color;
    ctx.lineWidth = (thick || 0.2) * 10;
    ctx.lineCap = 'round';
    // Use centroid as center
    const n = points.length;
    const sum = points.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 });
    const center = { x: sum.x / n, y: sum.y / n };
    withRotation(ctx, center, rotation, () => {
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) {
        ctx.lineTo(points[i].x, points[i].y);
      }
      ctx.stroke();
    });
  };
  const drawSquare = (ctx, start, end, color, thick, rotation) => {
    if (!start || !end) return;
    ctx.strokeStyle = color;
    ctx.lineWidth = (thick || 0.2) * 10;
    const x = start.x;
    const y = start.y;
    const w = end.x - start.x;
    const h = end.y - start.y;
    const center = { x: x + w / 2, y: y + h / 2 };
    withRotation(ctx, center, rotation, () => {
      ctx.strokeRect(x, y, w, h);
    });
  };
  const drawCircle = (ctx, start, end, color, thick, rotation) => {
    if (!start || !end) return;
    ctx.strokeStyle = color;
    ctx.lineWidth = (thick || 0.2) * 10;
    const x = (start.x + end.x) / 2;
    const y = (start.y + end.y) / 2;
    const rx = Math.abs(end.x - start.x) / 2;
    const ry = Math.abs(end.y - start.y) / 2;
    const center = { x, y };
    withRotation(ctx, center, rotation, () => {
      ctx.beginPath();
      ctx.ellipse(x, y, rx, ry, 0, 0, 2 * Math.PI);
      ctx.stroke();
    });
  };
  const drawLine = (ctx, start, end, color, thick, rotation) => {
    if (!start || !end) return;
    ctx.strokeStyle = color;
    ctx.lineWidth = (thick || 0.2) * 10;
    const center = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };
    withRotation(ctx, center, rotation, () => {
      ctx.beginPath();
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(end.x, end.y);
      ctx.stroke();
    });
  };
  const drawArrow = (ctx, start, end, color, thick, rotation) => {
    if (!start || !end) return;
    ctx.strokeStyle = color;
    ctx.lineWidth = (thick || 0.2) * 10;
    const center = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };
    withRotation(ctx, center, rotation, () => {
      // Draw main line
      ctx.beginPath();
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(end.x, end.y);
      ctx.stroke();
      // Draw arrowhead
      const headlen = 14;
      const dx = end.x - start.x;
      const dy = end.y - start.y;
      const angle = Math.atan2(dy, dx);
      ctx.beginPath();
      ctx.moveTo(end.x, end.y);
      ctx.lineTo(end.x - headlen * Math.cos(angle - Math.PI / 6), end.y - headlen * Math.sin(angle - Math.PI / 6));
      ctx.moveTo(end.x, end.y);
      ctx.lineTo(end.x - headlen * Math.cos(angle + Math.PI / 6), end.y - headlen * Math.sin(angle + Math.PI / 6));
      ctx.stroke();
    });
  };
  const drawPin = (ctx, pin) => {
    ctx.save();
    ctx.translate(pin.x, pin.y);
    ctx.rotate((pin.rotation || 0) * Math.PI / 180);
    
    // Draw pin head (circle) - simple style
    ctx.beginPath();
    ctx.arc(0, 0, 8, 0, 2 * Math.PI);
    ctx.fillStyle = pin.color || '#008080';
    
    // Add simple shadow to pin head
    ctx.shadowColor = 'rgba(0,0,0,0.2)';
    ctx.shadowBlur = 3;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 1;
    ctx.fill();
    
    // Reset shadow
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    
    // Draw pin number
    ctx.fillStyle = '#fff';
    ctx.font = '600 10px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(pin.number || '', 0, 0);
    
    // Draw pin shaft
    ctx.beginPath();
    ctx.rect(-1, 8, 2, 8);
    ctx.fillStyle = pin.color || '#008080';
    ctx.fill();
    
    // Draw pin tip
    ctx.beginPath();
    ctx.moveTo(-1, 16); // Left point of triangle
    ctx.lineTo(1, 16);  // Right point of triangle
    ctx.lineTo(0, 19);  // Bottom sharp point
    ctx.closePath();
    ctx.fillStyle = pin.color || '#008080';
    ctx.fill();
    
    ctx.restore();
  };

  const drawText = (ctx, textShape) => {
    if (!textShape.text) return;
    ctx.save();
    
    // Apply rotation if specified
    if (textShape.rotation && textShape.rotation !== 0) {
      ctx.translate(textShape.x, textShape.y);
      ctx.rotate((textShape.rotation * Math.PI) / 180);
      ctx.translate(-textShape.x, -textShape.y);
    }
    
    ctx.fillStyle = textShape.color || '#02414a';
    ctx.font = `${textShape.fontWeight || 'normal'} ${textShape.fontSize || 16}px sans-serif`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    
    // Add text shadow for better visibility
    ctx.shadowColor = 'rgba(0,0,0,0.3)';
    ctx.shadowBlur = 2;
    ctx.shadowOffsetX = 1;
    ctx.shadowOffsetY = 1;
    
    // Split text by lines and draw each line
    const lines = textShape.text.split('\n');
    lines.forEach((line, index) => {
      ctx.fillText(line, textShape.x, textShape.y + (index * (textShape.fontSize || 16) * 1.2));
    });
    
    ctx.restore();
  };

  async function generateResizedBase64Image(element, targetWidth, targetHeight) {
    // This function should generate a base64 image from the element at the given size
    // For now, fallback to the current save logic
    return new Promise((resolve) => {
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = targetWidth;
      tempCanvas.height = targetHeight;
      const ctx = tempCanvas.getContext('2d');

      const drawShapesAndPins = () => {
        shapes.forEach(shape => {
          if (shape.type === 'freehand') drawFreehand(ctx, shape.points, shape.color, shape.thickness, shape.rotation);
          if (shape.type === 'square') drawSquare(ctx, shape.start, shape.end, shape.color, shape.thickness, shape.rotation);
          if (shape.type === 'circle') drawCircle(ctx, shape.start, shape.end, shape.color, shape.thickness, shape.rotation);
          if (shape.type === 'line') drawLine(ctx, shape.start, shape.end, shape.color, shape.thickness, shape.rotation);
          if (shape.type === 'arrow') drawArrow(ctx, shape.start, shape.end, shape.color, shape.thickness, shape.rotation);
          if (shape.type === 'text') drawText(ctx, shape);
        });
        pins.forEach(pin => {
          drawPin(ctx, pin);
        });
        resolve(tempCanvas.toDataURL('image/png'));
      };

      if (isVideo) {
        ctx.fillStyle = '#061e22';
        ctx.fillRect(0, 0, targetWidth, targetHeight);
        drawShapesAndPins();
      } else {
        const img = new window.Image();
        img.onload = () => {
          ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
          drawShapesAndPins();
        };
        img.src = image;
      }
    });
  }

  const handleSaveImage = async () => {
    setLoading(true);
    // You can use a ref or document.getElementById if needed
    // const element = document.getElementById("image-container");
    const targetWidth = width;
    const targetHeight = height;
    const imageBase64 = await generateResizedBase64Image(null, targetWidth, targetHeight);
    if (!imageBase64) {
      setLoading(false);
      alert("Failed to generate image.");
      return;
    }
    const base64Data = imageBase64.replace(/^data:image\/png;base64,/, "");
    let url = "https://dev.salusemr.com/api/index?action=save_marked_image";
    let domain = "https://dev.salusemr.com/";
    if (serverValue === "prod") {
      url = "https://salusemr.com/api/index?action=save_marked_image";
      domain = "https://salusemr.com/";
    }
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        id,
        image_base64: base64Data,
        product_data: (productList || []).map(pin => (pin)),
        type: types,
        width: targetWidth,
        height: targetHeight,
      }),
    })
      .then((response) => {
        if (!response.ok) throw new Error("Network response was not ok");
        return response.json();
      })
      .then((data) => {
        console.log("Upload success:", data);
        setLoading(false);
        window.parent.postMessage({ type: 'UPLOAD_SUCCESS' }, '*');
        // Removed navigation
      })
      .catch((error) => {
        setLoading(false);
        console.error("Upload error:", error);
        alert("Failed to upload image.");
      });
  };

  return (
    <div style={{ textAlign: 'center'}}>
      <button className="save-btn" onClick={handleSaveImage}>
        Save Image
      </button>
    </div>
  );
}

export default SaveButton; 